'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { fetchTaskById, type TaskSelfItem } from '@/client/api/tasksSelf';
import { TaskStatus } from '@/client/hooks/useTaskManager';
import { DEFAULT_POLL_POLICY } from '@/client/config/polling.config';
import { parseTaskProgress, normalizeTaskStatus } from '@/client/utils/taskPollingUtils';
import { useStoryboard } from '../context';
import type {
  StoryboardSegment,
  StoryboardSegmentPreviewEntry,
  StoryboardSegmentStatus,
  StoryboardSegmentTask,
  StoryboardPreviewDownloadSource,
  StoryboardPreviewSourceMetadata,
} from '../types';
import {
  createPreviewEntry,
  extractVideoUrlFromResult,
  resolvePreviewEntryDisplayUrl,
} from '../utils/mediaHelpers';
import {
  extractSoraPreviewSourceMetadataFromTask,
  extractSoraVideoIdFromTask,
  isSoraModelId,
} from '../utils/soraHelpers';

const STATUS_CANDIDATE_KEYS = ['status', 'state', 'run_status', 'task_status', 'phase'];
const MAX_STATUS_SCAN_DEPTH = 4;

const makeTaskKey = (segmentId: string, taskId: string): string => `${segmentId}::${taskId}`;
const isActiveStatus = (status?: string | null): boolean =>
  status === 'queued' || status === 'generating';
const toTaskStatus = (
  status?: StoryboardSegmentStatus
): StoryboardSegmentTask['status'] => {
  switch (status) {
    case 'generating':
      return 'generating';
    case 'success':
      return 'success';
    case 'failed':
      return 'failed';
    default:
      return 'queued';
  }
};

const isSameSourceMetadata = (
  a: StoryboardPreviewSourceMetadata | undefined,
  b: StoryboardPreviewSourceMetadata | undefined
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    (a.endpoint ?? '') === (b.endpoint ?? '') &&
    (a.modelId ?? '') === (b.modelId ?? '') &&
    (a.modelKey ?? '') === (b.modelKey ?? '') &&
    (a.isPrivate ?? null) === (b.isPrivate ?? null)
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeSegmentStatus = (status?: string | null): StoryboardSegmentStatus => {
  const taskStatus = normalizeTaskStatus(status);
  switch (taskStatus) {
    case TaskStatus.SUBMITTING:
    case TaskStatus.PENDING:
      return 'queued';
    case TaskStatus.RUNNING:
      return 'generating';
    case TaskStatus.SUCCESS:
      return 'success';
    case TaskStatus.FAILED:
      return 'failed';
    default:
      return 'queued';
  }
};

const findStatusCandidate = (value: unknown, depth = 0): string | undefined => {
  if (!value || typeof value !== 'object' || depth > MAX_STATUS_SCAN_DEPTH) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  for (const key of STATUS_CANDIDATE_KEYS) {
    const candidate = record[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  for (const candidate of Object.values(record)) {
    if (candidate && typeof candidate === 'object') {
      const nested = findStatusCandidate(candidate, depth + 1);
      if (nested) {
        return nested;
      }
    }
  }
  return undefined;
};

const resolveTaskStatus = (item: TaskSelfItem): string | undefined => {
  if (typeof item.status === 'string' && item.status.trim()) {
    return item.status.trim();
  }
  if (item.data && isRecord(item.data)) {
    const fromData = findStatusCandidate(item.data);
    if (fromData) {
      return fromData;
    }
  }
  return findStatusCandidate(item);
};

const findMatchingPreviewEntry = (
  segment: StoryboardSegment,
  criteria: {
    url?: string | null;
    taskId?: string | null;
    downloadSource?: StoryboardPreviewDownloadSource;
  }
): StoryboardSegmentPreviewEntry | null => {
  const { url, taskId, downloadSource } = criteria;
  return (
    segment.previewEntries.find((entry) => {
      if (url && entry.url === url) {
        return true;
      }
      if (taskId && entry.taskId && entry.taskId === taskId) {
        return true;
      }
      if (
        downloadSource &&
        entry.downloadSource?.type === downloadSource.type &&
        entry.downloadSource.videoId === downloadSource.videoId
      ) {
        return true;
      }
      return false;
    }) ?? null
  );
};

const StoryboardTaskMonitor: React.FC = () => {
  const { segments, updateSegment } = useStoryboard();
  const timersRef = useRef<Record<string, number>>({});
  const attemptsRef = useRef<Record<string, number>>({});
  const inFlightRef = useRef<Set<string>>(new Set());
  const latestSegmentsRef = useRef<StoryboardSegment[]>(segments);

  useEffect(() => {
    latestSegmentsRef.current = segments;
  }, [segments]);

  const activeTasks = useMemo(() => {
    const items: Array<{ segmentId: string; taskId: string }> = [];
    const seen = new Set<string>();
    segments.forEach((segment) => {
      const taskList: StoryboardSegmentTask[] = Array.isArray(segment.tasks)
        ? segment.tasks
        : [];
      for (const task of taskList) {
        if (!task.taskId || !isActiveStatus(task.status)) {
          continue;
        }
        const key = makeTaskKey(segment.segmentId, task.taskId);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        items.push({ segmentId: segment.segmentId, taskId: task.taskId });
      }
      if (!taskList.length && segment.taskId && isActiveStatus(segment.status)) {
        const key = makeTaskKey(segment.segmentId, segment.taskId);
        if (!seen.has(key)) {
          seen.add(key);
          items.push({ segmentId: segment.segmentId, taskId: segment.taskId });
        }
      }
    });
    return items;
  }, [segments]);

  const stopInterval = (segmentId: string, taskId: string) => {
    const key = makeTaskKey(segmentId, taskId);
    const timerId = timersRef.current[key];
    if (timerId !== undefined) {
      window.clearInterval(timerId);
      delete timersRef.current[key];
    }
    delete attemptsRef.current[key];
  };

  const pollOnce = useCallback(
    async (segmentId: string, taskId: string) => {
      const key = makeTaskKey(segmentId, taskId);
      if (inFlightRef.current.has(key)) {
        return;
      }
      const attempts = (attemptsRef.current[key] ?? 0) + 1;
      attemptsRef.current[key] = attempts;
      const maxAttempts = DEFAULT_POLL_POLICY.maxAttempts ?? 300;

      if (attempts > maxAttempts) {
        console.error(
          `[storyboard] 轮询任务 ${taskId} 超时(超过 ${maxAttempts} 次尝试)`
        );
        stopInterval(segmentId, taskId);

        const current = latestSegmentsRef.current.find(
          (item) => item.segmentId === segmentId
        );
        if (current) {
          const currentTasks: StoryboardSegmentTask[] = Array.isArray(current.tasks)
            ? current.tasks
            : [];
          try {
            await updateSegment(
              current.segmentId,
              {
                status: 'failed' as StoryboardSegmentStatus,
                tasks: currentTasks.map((t) =>
                  t.taskId === taskId
                    ? { ...t, status: 'failed' as const, failReason: '轮询超时' }
                    : t
                ),
              },
              { silent: true }
            );
          } catch (updateError) {
            console.error('[storyboard] 更新segment状态失败(轮询超时)', updateError);
          }
        }
        return;
      }
      const current = latestSegmentsRef.current.find((item) => item.segmentId === segmentId);
      if (!current) {
        stopInterval(segmentId, taskId);
        return;
      }
      const taskList: StoryboardSegmentTask[] = Array.isArray(current.tasks)
        ? current.tasks
        : [];
      let workingTasks = taskList;
      let currentTask = taskList.find((taskEntry) => taskEntry.taskId === taskId) ?? null;
      let fallbackTask: StoryboardSegmentTask | null = null;
      const createdFallbackTask = !currentTask && current.taskId === taskId;
      if (createdFallbackTask) {
        fallbackTask = {
          taskId,
          status: toTaskStatus(current.status),
          progress:
            typeof current.progress === 'number' ? Math.max(0, Math.min(100, Math.round(current.progress))) : undefined,
          submittedAt: current.lastUpdated ?? Date.now(),
          updatedAt: current.lastUpdated ?? Date.now(),
        };
        currentTask = fallbackTask;
        workingTasks = [...taskList, fallbackTask];
      }
      if (!currentTask || !isActiveStatus(currentTask.status)) {
        stopInterval(segmentId, taskId);
        return;
      }
      inFlightRef.current.add(key);
      try {
        const task = await fetchTaskById(taskId);
        if (!task) {
          return;
        }

        const now = Date.now();
        let nextStatus = normalizeSegmentStatus(resolveTaskStatus(task));
        if (nextStatus === 'idle' || nextStatus === 'draft') {
          nextStatus = 'queued';
        }
        let nextTaskStatus: StoryboardSegmentTask['status'];
        if (nextStatus === 'success' || nextStatus === 'failed') {
          nextTaskStatus = nextStatus;
        } else if (nextStatus === 'generating') {
          nextTaskStatus = 'generating';
        } else {
          nextTaskStatus = 'queued';
        }

        const progress = parseTaskProgress(task.progress);
        if (progress !== undefined && progress > 0 && nextTaskStatus === 'queued') {
          nextTaskStatus = 'generating';
        }
        const failReason =
          typeof task.fail_reason === 'string' && task.fail_reason.trim()
            ? task.fail_reason.trim()
            : undefined;
        if (failReason) {
          nextTaskStatus = 'failed';
        }

        const updates: Partial<StoryboardSegment> = {};
        let tasksUpdated = false;

        let updatedTasks = workingTasks.map((item) => {
          if (item.taskId !== taskId) {
            return item;
          }
          const isNewlyAddedTask = fallbackTask !== null && item === fallbackTask;
          let nextProgress: number | undefined;
          if (nextTaskStatus === 'success') {
            nextProgress = 100;
          } else if (progress !== undefined) {
            nextProgress = progress;
          } else if (isActiveStatus(nextTaskStatus)) {
            nextProgress =
              typeof item.progress === 'number' ? Math.max(0, Math.min(100, Math.round(item.progress))) : undefined;
          } else {
            nextProgress = undefined;
          }
          const nextFailReason = failReason;
          const currentFailReason =
            typeof item.failReason === 'string' && item.failReason.trim()
              ? item.failReason.trim()
              : undefined;
          const shouldUpdateTask =
            isNewlyAddedTask ||
            item.status !== nextTaskStatus ||
            (item.progress ?? undefined) !== nextProgress ||
            currentFailReason !== nextFailReason;
          if (!shouldUpdateTask) {
            return item;
          }
          tasksUpdated = true;
          return {
            ...item,
            status: nextTaskStatus,
            progress: nextProgress,
            failReason: nextFailReason,
            updatedAt: now,
          };
        });

        if (nextTaskStatus === 'success') {
          const videoUrl =
            extractVideoUrlFromResult(task.data) ??
            extractVideoUrlFromResult(task.properties) ??
            extractVideoUrlFromResult(task);

          const metadataCandidate = extractSoraPreviewSourceMetadataFromTask(task);
          const metadataImpliesSora =
            (metadataCandidate?.modelId && isSoraModelId(metadataCandidate.modelId)) ||
            (metadataCandidate?.modelKey && isSoraModelId(metadataCandidate.modelKey));
          const treatAsSora = isSoraModelId(current.modelId) || metadataImpliesSora;

          let downloadSource: StoryboardPreviewDownloadSource | undefined;
          let sourceMetadata: StoryboardPreviewSourceMetadata | undefined;
          if (treatAsSora) {
            const videoId = extractSoraVideoIdFromTask(task, [taskId, videoUrl]);
            if (videoId) {
              downloadSource = { type: 'sora', videoId };
            }
            sourceMetadata = metadataCandidate;
          }

          if (videoUrl) {
            const existing = findMatchingPreviewEntry(current, {
              url: videoUrl,
              taskId,
            });
            const sourcePreviewEntries = updates.previewEntries ?? current.previewEntries;
            if (!existing) {
              const newEntry = createPreviewEntry(current.segmentId, videoUrl, {
                taskId,
                downloadSource,
                ...(sourceMetadata ? { sourceMetadata } : {}),
              });
              updates.previewEntries = [newEntry, ...sourcePreviewEntries];
              updates.previewUrl = resolvePreviewEntryDisplayUrl(newEntry);
              updates.selectedPreviewId = newEntry.previewId;
              updatedTasks = updatedTasks.map((item) =>
                item.taskId === taskId ? { ...item, previewId: newEntry.previewId } : item
              );
              tasksUpdated = true;
            } else {
              let effectiveExisting = existing;
              const shouldUpdateDownloadSource =
                downloadSource &&
                (!existing.downloadSource ||
                  existing.downloadSource.type !== downloadSource.type ||
                  existing.downloadSource.videoId !== downloadSource.videoId);
              const shouldUpdateSourceMetadata =
                sourceMetadata && !isSameSourceMetadata(existing.sourceMetadata, sourceMetadata);

              if (shouldUpdateDownloadSource || shouldUpdateSourceMetadata) {
                const updatedEntries = sourcePreviewEntries.map((entry) => {
                  if (entry.previewId !== existing.previewId) {
                    return entry;
                  }
                  return {
                    ...entry,
                    ...(shouldUpdateDownloadSource ? { downloadSource } : {}),
                    ...(shouldUpdateSourceMetadata ? { sourceMetadata } : {}),
                  };
                });
                updates.previewEntries = updatedEntries;
                effectiveExisting =
                  updatedEntries.find((entry) => entry.previewId === existing.previewId) ??
                  existing;
              }

              if (!current.previewUrl) {
                updates.previewUrl = resolvePreviewEntryDisplayUrl(effectiveExisting);
                updates.selectedPreviewId = effectiveExisting.previewId;
                updatedTasks = updatedTasks.map((item) => {
                  if (item.taskId !== taskId || item.previewId === effectiveExisting.previewId) {
                    return item;
                  }
                  tasksUpdated = true;
                  return { ...item, previewId: effectiveExisting.previewId };
                });
              } else {
                updatedTasks = updatedTasks.map((item) => {
                  if (item.taskId !== taskId || item.previewId === effectiveExisting.previewId) {
                    return item;
                  }
                  tasksUpdated = true;
                  return { ...item, previewId: effectiveExisting.previewId };
                });
              }
            }
          } else if (downloadSource) {
            const existing = findMatchingPreviewEntry(current, {
              downloadSource,
              taskId,
            });
            const sourcePreviewEntries = updates.previewEntries ?? current.previewEntries;
            if (!existing) {
              const newEntry = createPreviewEntry(current.segmentId, undefined, {
                taskId,
                downloadSource,
                ...(sourceMetadata ? { sourceMetadata } : {}),
              });
              updates.previewEntries = [newEntry, ...sourcePreviewEntries];
              updates.previewUrl = resolvePreviewEntryDisplayUrl(newEntry);
              updates.selectedPreviewId = newEntry.previewId;
              updatedTasks = updatedTasks.map((item) =>
                item.taskId === taskId ? { ...item, previewId: newEntry.previewId } : item
              );
              tasksUpdated = true;
            } else {
              const shouldUpdateSourceMetadata =
                sourceMetadata && !isSameSourceMetadata(existing.sourceMetadata, sourceMetadata);
              if (shouldUpdateSourceMetadata) {
                const updatedEntries = sourcePreviewEntries.map((entry) => {
                  if (entry.previewId !== existing.previewId) {
                    return entry;
                  }
                  return {
                    ...entry,
                    sourceMetadata,
                  };
                });
                updates.previewEntries = updatedEntries;
              }
              const effectiveEntries = updates.previewEntries ?? current.previewEntries;
              const refreshedExisting =
                effectiveEntries.find((entry) => entry.previewId === existing.previewId) ?? existing;

              if (!current.previewUrl) {
                updates.previewUrl = resolvePreviewEntryDisplayUrl(refreshedExisting);
                updates.selectedPreviewId = refreshedExisting.previewId;
              }
              updatedTasks = updatedTasks.map((item) => {
                if (item.taskId !== taskId || item.previewId === refreshedExisting.previewId) {
                  return item;
                }
                tasksUpdated = true;
                return { ...item, previewId: refreshedExisting.previewId };
              });
            }
          }
        }

        if (tasksUpdated) {
          const sortedTasks = [...updatedTasks].sort((a, b) => a.submittedAt - b.submittedAt);
          const latestTask = sortedTasks[sortedTasks.length - 1] ?? null;
          const activePrimary =
            [...sortedTasks]
              .reverse()
              .find((taskEntry) => isActiveStatus(taskEntry.status)) ?? null;
          const primaryTask = activePrimary ?? latestTask;
          const derivedStatus: StoryboardSegmentStatus = primaryTask
            ? (primaryTask.status as StoryboardSegmentStatus)
            : current.status;
          const derivedTaskId = primaryTask?.taskId;
          let derivedProgress: number | undefined;
          if (!primaryTask) {
            derivedProgress = undefined;
          } else if (primaryTask.status === 'success') {
            derivedProgress = 100;
          } else if (primaryTask.status === 'failed') {
            derivedProgress = undefined;
          } else {
            derivedProgress =
              typeof primaryTask.progress === 'number' ? primaryTask.progress : undefined;
          }
          updates.tasks = updatedTasks;
          if (current.taskId !== derivedTaskId) {
            updates.taskId = derivedTaskId;
          }
          if (current.status !== derivedStatus) {
            updates.status = derivedStatus;
          }
          if (current.progress !== derivedProgress) {
            updates.progress = derivedProgress;
          }
        }

        if (Object.keys(updates).length > 0) {
          await updateSegment(current.segmentId, updates, { silent: true });
        }

        if (nextTaskStatus === 'success' || nextTaskStatus === 'failed') {
          stopInterval(segmentId, taskId);
        }
      } catch (error) {
        console.error('[storyboard] 轮询任务状态失败', error);

        const current = latestSegmentsRef.current.find((item) => item.segmentId === segmentId);
        if (!current) {
          return;
        }
        const attempts = attemptsRef.current[key] ?? 0;
        if (attempts > 5) {
          stopInterval(segmentId, taskId);
          const currentTasks: StoryboardSegmentTask[] = Array.isArray(current.tasks)
            ? current.tasks
            : [];
          try {
            await updateSegment(
              current.segmentId,
              {
                status: 'failed' as StoryboardSegmentStatus,
                tasks: currentTasks.map((t) =>
                  t.taskId === taskId
                    ? { ...t, status: 'failed' as const, failReason: '轮询失败' }
                    : t
                ),
              },
              { silent: true }
            );
          } catch (updateError) {
            console.error('[storyboard] 更新segment状态失败(轮询异常)', updateError);
          }
        }
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [updateSegment]
  );

  useEffect(() => {
    const activeKeys = new Set(
      activeTasks.map(({ segmentId, taskId }) => makeTaskKey(segmentId, taskId))
    );

    activeTasks.forEach(({ segmentId, taskId }) => {
      const key = makeTaskKey(segmentId, taskId);
      if (timersRef.current[key] !== undefined) {
        return;
      }
      void pollOnce(segmentId, taskId);
      const timerId = window.setInterval(() => {
        void pollOnce(segmentId, taskId);
      }, DEFAULT_POLL_POLICY.intervalMs ?? 10000);
      timersRef.current[key] = timerId;
    });

    Object.entries(timersRef.current).forEach(([key, timerId]) => {
      if (!activeKeys.has(key)) {
        window.clearInterval(timerId);
        delete timersRef.current[key];
        delete attemptsRef.current[key];
      }
    });

    return () => {
      Object.values(timersRef.current).forEach((timerId) => {
        window.clearInterval(timerId);
      });
      timersRef.current = {};
      attemptsRef.current = {};
    };
  }, [activeTasks, pollOnce]);

  return null;
};

export default StoryboardTaskMonitor;
